import { createGeneratedTrackMethods, type GeneratedTrackMethods } from './trackMethodsGenerated';

import type { CventActivitiesBaseProperties } from './core';

/** Minimal structural shape of a Vuex action context that can publish Cvent facts. */
export interface CventAnalyticsActionContext {
  rootGetters: {
    'event/isBroadcastSpeakerOrOrganizer'?: boolean;
    'event/getCventIdentity'?: CventActivitiesBaseProperties;
  };
}

/** Minimal shape of a persisted Q&A question post (Mattermost post). */
export interface CventQuestionPost {
  id?: string;
}

/** Extra fact properties known at the call site but not on the persisted post. */
export interface CventQuestionAskedMetadata {
  isAnonymous?: boolean;
}

/**
 * The fact's sessionId must be the Cvent session identifier — sourced from the
 * survey endpoint and exposed via `event/getCventIdentity` (WEBINAR-67983) —
 * NOT `route.params.id`, which is the Goldcast broadcast id. Using the route id
 * would attribute `session.question_asked` facts to the broadcast instead of
 * the Cvent session.
 */
function resolveSessionId(context: CventAnalyticsActionContext): string | undefined {
  return context.rootGetters['event/getCventIdentity']?.sessionId ?? undefined;
}

function createStoreBackedTrackMethods(context: CventAnalyticsActionContext): GeneratedTrackMethods {
  return createGeneratedTrackMethods({
    isAttendee: () => !context.rootGetters['event/isBroadcastSpeakerOrOrganizer'],
    collectBaseProperties: () => context.rootGetters['event/getCventIdentity'] as CventActivitiesBaseProperties,
    initialize: () => undefined
  });
}

/**
 * Publishes the `session.question_asked` fact for a persisted Q&A question.
 *
 * Both `sessionId` and `questionId` are contract-required (see
 * `actions/question_asked.json`), so the fact is skipped when either is
 * missing.
 *
 * Returns `true` when a fact was handed to the analytics core, `false` when it
 * was skipped (non-attendee, missing session/question id, incomplete Cvent
 * identity, or pipeline disabled).
 */
function trackQuestionAsked(
  context: CventAnalyticsActionContext,
  questionPost: CventQuestionPost | null | undefined,
  metadata: CventQuestionAskedMetadata = {}
): boolean {
  const sessionId = resolveSessionId(context);
  const questionId = questionPost?.id;
  if (!sessionId || !questionId) {
    return false;
  }

  return createStoreBackedTrackMethods(context).trackQuestionAsked({
    sessionId,
    questionId,
    ...metadata
  });
}

export const cventAnalytics = {
  trackQuestionAsked
};
